import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { Plus } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetAccountList } from "@/services/api";
import { DrAccount } from "@/services/model";

import { BillingAccountChip } from "./BillingAccountChip";
import { BillingAccountItem } from "./BillingAccountItem/BillingAccountItem";
import { BillingAccountModal } from "./BillingAccountModal/BillingAccountModal";
import {
  BillingAccountSubscription,
  BulkMoveLocationState,
  sortBillingAccounts,
} from "./billingAccountUtils";
import { MoveSubscriptionModal } from "./MoveSubscriptionModal/MoveSubscriptionModal";

/** `account` set = edit that Vertragskonto, unset = create a new one. */
type ModalState = { account?: DrAccount };

type MoveState = {
  source: DrAccount;
  subscription: BillingAccountSubscription;
};

/**
 * Rechnungsempfänger: the customer's Vertragskonten (billing accounts) as a
 * full-width list. Replaces the payment-method card for invoice customers.
 */
export const BillingAccounts: FC = () => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts",
  });
  const { t: tPortal } = useTranslation("portal");
  const { t: tGlobal } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [moveState, setMoveState] = useState<MoveState | null>(null);
  // Only one Vertragskonto picks Abos at a time; the others are hidden so no
  // Abo of a different source can end up in the Sammelverschiebung.
  const [selectingAccountId, setSelectingAccountId] = useState<number | null>(
    null,
  );
  // Shown inline here instead of the global modal: an active bill run or the
  // account settings block every Vertragskonto change for up to 24 hours.
  const [isBlocked, setIsBlocked] = useState(false);
  const {
    data: unsortedAccounts,
    isPending: isLoading,
    isError,
  } = useGetAccountList({ expanded: true });
  const accounts = unsortedAccounts && sortBillingAccounts(unsortedAccounts);

  // A failed request is not "no accounts" - the global query error handler
  // already reports it, so the empty-state copy must not show as well.
  const isEmpty = !isLoading && !isError && accounts?.length === 0;
  const canMove = (accounts?.length ?? 0) > 1;
  const visibleAccounts =
    selectingAccountId == null
      ? accounts
      : accounts?.filter((account) => account.id === selectingAccountId);

  const startBulkMove = (sourceId: number, simIds: number[]) => {
    const state: BulkMoveLocationState = {
      sourceId,
      simIds,
      returnTo: pathname,
    };

    navigate(portalRoutes.documents.bulkMove, { state });
  };

  return (
    <div
      data-testid="billing-accounts"
      className="flex flex-col rounded-lg bg-surface-accent-ivory"
    >
      <div className="flex items-start gap-3 px-4 pb-2 pt-4">
        <div className="flex min-w-0 flex-1 items-center gap-2 py-2">
          <h2 className="truncate text-body-base font-semibold leading-md text-foreground-primary">
            {tPortal("user-account.tabs.billingRecipients")}
          </h2>
          <BillingAccountChip text={accounts?.length ?? 0} />
        </div>
        {selectingAccountId == null && (
          <Button
            dataTestid="billing-accounts-add"
            size="sm"
            icon={<Plus />}
            onClick={() => setModalState({})}
          >
            {t("add")}
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4 px-4 pb-4 pt-2">
        {isBlocked && (
          <Notification
            dataTestid="billing-accounts-blocked"
            tone="warning"
            title={tGlobal("billingActionBlocked.title")}
            description={tGlobal("billingActionBlocked.text")}
          />
        )}
        {isLoading ? (
          <div className="flex justify-center py-6">
            <LocalLoadingSpinner />
          </div>
        ) : isEmpty ? (
          <p className="py-5 text-body-sm text-foreground-secondary">
            {t("empty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {visibleAccounts?.map((account, index) => {
              const accountId = account.id;

              return (
                <li key={accountId ?? index} className="group">
                  <BillingAccountItem
                    account={account}
                    onEdit={() => setModalState({ account })}
                    onMove={
                      canMove
                        ? (subscription) =>
                            setMoveState({ source: account, subscription })
                        : undefined
                    }
                    onBulkMove={
                      canMove && accountId != null
                        ? (simIds) => startBulkMove(accountId, simIds)
                        : undefined
                    }
                    isSelecting={
                      accountId != null && accountId === selectingAccountId
                    }
                    onSelectingChange={(isSelecting) =>
                      setSelectingAccountId(
                        isSelecting ? (accountId ?? null) : null,
                      )
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {modalState && (
        <BillingAccountModal
          account={modalState.account}
          onClose={() => setModalState(null)}
          onBlocked={() => setIsBlocked(true)}
        />
      )}
      {moveState && (
        <MoveSubscriptionModal
          subscription={moveState.subscription}
          source={moveState.source}
          accounts={accounts ?? []}
          onClose={() => setMoveState(null)}
          onBlocked={() => setIsBlocked(true)}
        />
      )}
    </div>
  );
};
